import { duckDbIdentifier } from '@cotera/client/app/etc/duckdb';

export const DATASET_CUSTOM_QUERY_TABLE = 'dataset';

/** The preview's default source, shown pre-filled in the footer. */
export const DEFAULT_DATASET_QUERY = `SELECT * FROM ${DATASET_CUSTOM_QUERY_TABLE}`;

/**
 * Wraps the current parquet-backed preview table as a CTE named `dataset` so
 * ad-hoc footer queries can select from it. Returns null when the statement is
 * empty or is not a read-only SELECT/WITH.
 */
const resolveCustomSelectSql = (
  customSelectSql: string,
  tableName: string
): { sql: string } | { error: string } | null => {
  const trimmed = customSelectSql.trim().replace(/;+\s*$/, '');
  if (trimmed === '') {
    return null;
  }
  const upper = trimmed.toUpperCase();
  const cte = `${duckDbIdentifier(
    DATASET_CUSTOM_QUERY_TABLE
  )} AS (SELECT * FROM ${duckDbIdentifier(tableName)})`;
  if (upper.startsWith('WITH ')) {
    return { sql: `WITH ${cte}, ${trimmed.slice('WITH '.length)}` };
  }
  if (upper.startsWith('SELECT')) {
    return { sql: `WITH ${cte} ${trimmed}` };
  }
  return { error: 'Only SELECT statements are supported' };
};

/**
 * The alias every preview read gives its source, and therefore the name a
 * predicate uses to address the row as a whole.
 */
export const DATASET_PREVIEW_SOURCE_ALIAS = 'dataset_query';

/**
 * Whether the statement is still the one the preview arrives with, and so
 * reads the materialized table more or less directly.
 *
 * Two callers ask this, about two different columns of the projection, and
 * both are exported under their own name below — a reader asking either
 * question should not have to know that the same test answers both.
 */
const isDefaultStatement = (customSelectSql: string): boolean => {
  const trimmed = customSelectSql.trim().replace(/;+\s*$/, '');
  return (
    trimmed === '' ||
    trimmed.toUpperCase() === DEFAULT_DATASET_QUERY.toUpperCase()
  );
};

/**
 * True when the source still carries the preview's order column.
 *
 * An arbitrary user query may select any shape at all — `SELECT name FROM
 * dataset` drops the order column entirely — so anything beyond the default is
 * left unordered rather than referencing a column that may not be there.
 */
export const carriesPreviewOrder = (customSelectSql: string): boolean =>
  isDefaultStatement(customSelectSql);

/*
 * Deliberately not widened the way `projectsDatasetColumns` was. This one also
 * decides whether the preview appends an ORDER BY of its own, and a reader who
 * wrote `SELECT * FROM dataset ORDER BY name` has said how they want the rows
 * arranged. Treating that as ordered would clobber it with the bookkeeping
 * column's order instead.
 */

/**
 * A statement that keeps every column of the `dataset` CTE: `SELECT *` taken
 * straight from it, whatever follows.
 *
 * `WHERE`, `ORDER BY`, `LIMIT` and even a join can follow, because none of them
 * removes a column from the projection — a join only adds more. What the anchor
 * rules out is the two shapes that do: a narrowed select list, and a leading
 * `WITH`, where `SELECT *` may be starring a CTE of the reader's own that has
 * nothing to do with the dataset.
 *
 * `\b` after the name matters: `dataset_other` is a different table, and the
 * preview's DuckDB holds more than one dataset at a time.
 */
const SELECTS_ALL_FROM_DATASET = /^SELECT\s+\*\s+FROM\s+dataset\b/iu;

/**
 * True when the source projects every column the dataset's schema declares.
 *
 * The same hazard as the order column, for the user's own columns: a statement
 * that narrows the projection leaves a predicate built from the schema naming
 * columns the source no longer has, which DuckDB rejects outright rather than
 * ignoring.
 *
 * Wider than {@link carriesPreviewOrder} on purpose, which is what the two
 * names were split for. The fallback predicate this guards is correct but
 * costs two to five times the per-column one, and `SELECT * FROM dataset WHERE
 * ...` is the overwhelmingly common thing a reader types — every column is
 * still there, so there is nothing to fall back from.
 */
export const projectsDatasetColumns = (customSelectSql: string): boolean => {
  const trimmed = customSelectSql.trim().replace(/;+\s*$/, '');
  return trimmed === '' || SELECTS_ALL_FROM_DATASET.test(trimmed);
};

class DatasetPreviewQueryError extends Error {}

/**
 * The subquery every preview read (rows, counts, stats) selects from: either
 * the materialized preview table or the user's ad-hoc statement, with the
 * layer stack's projections wrapped around it.
 *
 * Wrapping is `DuckDbLayerStack.wrapSource`'s job rather than this function's,
 * and it wraps rather than appends: that makes each layer's values columns of a
 * subquery, which is what lets the untouched WHERE/ORDER BY built from the
 * grid's own sorts and filters address them. A WHERE cannot reference a SELECT
 * alias; it can reference a subquery's column.
 */
export const datasetPreviewSourceSql = (
  tableName: string,
  customSelectSql: string,
  wrapSource: (baseSql: string) => string = (baseSql) => baseSql
): string => {
  const resolved = resolveCustomSelectSql(customSelectSql, tableName);
  if (resolved !== null && 'error' in resolved) {
    throw new DatasetPreviewQueryError(resolved.error);
  }
  const base =
    resolved === null
      ? `(SELECT * FROM ${duckDbIdentifier(tableName)})`
      : `(${resolved.sql})`;
  return wrapSource(base);
};
